import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { User } from '@nousmedis/guard-types/frontend';
import { AuthModel } from '@nousmedis/nm-guard-auth';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { UserService } from '../../services/user.service';
import { AppEntry } from '../app-selector/app-selector.component';
import { LoginCredentials, UserLoginFormComponent } from '../user-login-form/user-login-form.component';
import { fadeSlide } from '../../../../base-animations';

@Component({
  selector: 'sw-user-login',
  templateUrl: './user-login.component.html',
  styleUrls: ['./user-login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeSlide],
})
export class UserLoginComponent implements OnInit {
  @ViewChild(UserLoginFormComponent) loginFormRef?: UserLoginFormComponent;

  user: User | undefined;
  appsInOrg: AppEntry[] = [];
  loadingApps = false;
  isLoginLoading = false;

  showResetModal = false;
  resetEmail = '';

  private readonly auth = inject(AuthModel);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.translate.setDefaultLang('es_ES');
    this.user = this.auth.getCurrentUser() ?? undefined;
    if (this.user) {
      this.loadApps(this.user.id);
    }
  }

  async onLoginSubmit(credentials: LoginCredentials): Promise<void> {
    this.isLoginLoading = true;
    this.cdr.markForCheck();

    try {
      const payload = await this.auth.tryLogin(
        credentials.username,
        credentials.password,
        credentials.remember
      );
      const decoded = this.auth.decodeToken(payload.token);
      const userLoged = this.auth.getUserFromToken(payload.token);

      if (decoded) {
        this.auth.setTokenCookie(payload.token);
      }

      this.user = userLoged.user as User;
      const orgs = await this.auth.getAllOrgsOfUser(this.user.id);
      this.auth.setOrgs(orgs, this.user.id);
      this.userService.setOrgs(orgs);
      this.setAppsForPage(orgs);
    } catch {
      // Agitar la card de login para feedback visual inmediato
      this.loginFormRef?.triggerShake();

      this.translate.get('USER.BAD_LOGIN').subscribe((res: string) => {
        this.messageService.add({
          key: 'base-messages',
          severity: 'error',
          summary: 'Error',
          detail: res,
        });
      });
    } finally {
      this.isLoginLoading = false;
      this.cdr.markForCheck();
    }
  }

  onForgotPassword(email: string): void {
    this.resetEmail = email ?? '';
    this.showResetModal = true;
    this.cdr.markForCheck();
  }

  async onAppSelected(app: AppEntry): Promise<void> {
    if (!this.user) return;
    const authUrl = await this.auth.getAuthAppLink(this.user.id, app.orgId, app.appId);
    window.location.href = authUrl;
  }

  onLogout(): void {
    this.auth.logOut(this.user);
    this.userService.user$.next(undefined);
    this.router.navigate(['/login']);
  }

  private async loadApps(userId: string): Promise<void> {
    const orgs = await this.auth.getAllOrgsOfUser(userId);
    this.auth.setOrgs(orgs, userId);
    this.setAppsForPage(orgs);
  }

  private setAppsForPage(orgs: any[]): void {
    this.loadingApps = true;
    const apps: AppEntry[] = orgs
      .flatMap((o: any) =>
        (o.services?.ccserver ?? []).map((s: any) => ({
          orgId: o.id as string,
          orgName: o.name as string,
          appId: s.container.id as string,
          appName: s.name as string,
          status: 'running',
        }))
      )
      .filter(Boolean);

    this.appsInOrg = apps;
    this.loadingApps = false;
    this.cdr.markForCheck();

    if (apps.length === 1) {
      this.onAppSelected(apps[0]);
    }
  }
}
